/**
 * WordsContext — fetches all word data from the backend API.
 * Games subscribe to this context to get words grouped by language + category.
 */
import { createContext, useContext, useCallback, useEffect, useReducer, useState } from "react";
import { wordsApi } from "../api/client";
import { preloadManifest } from "../utils/speech";
import { useApp } from "./AppContext";

const WordsContext = createContext(null);

const initial = {
  letters: [],          // HebrewLetter[]
  hebrewWords: {},      // { [category]: Word[] }
  englishWords: {},     // { [category]: Word[] }
  loading: true,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "LOADED":
      return { ...state, loading: false, letters: action.letters,
               hebrewWords: action.hebrewWords, englishWords: action.englishWords };
    case "ERROR":
      return { ...state, loading: false, error: action.payload };
    default: return state;
  }
}

function groupByCategory(words) {
  return words.reduce((acc, w) => {
    if (!acc[w.category]) acc[w.category] = [];
    acc[w.category].push(w);
    return acc;
  }, {});
}

export function WordsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initial);
  // Word DB + audio manifest are only needed inside the app. Don't download
  // thousands of rows + preload audio for anonymous marketing-page visitors.
  //
  // A public /games/:id page is the one exception: ten of the games draw their
  // content from this DB, and a visitor playing one there has no token. They
  // call requestWords() when such a game mounts, so the download still never
  // happens for someone merely reading an article or a landing page.
  const { token } = useApp() || {};
  const [fetched, setFetched] = useState(false);
  const [requested, setRequested] = useState(false);
  const requestWords = useCallback(() => setRequested(true), []);

  useEffect(() => {
    if ((!token && !requested) || fetched) return;
    setFetched(true);
    (async () => {
      try {
        // Load audio manifest first (API returns letter/nikud keys first for preload order).
        // Await so games never speak TTS while manifest is still in flight.
        await preloadManifest();
        const [letters, heWords, enWords] = await Promise.all([
          wordsApi.letters(),
          wordsApi.list({ lang: "he" }),
          wordsApi.list({ lang: "en" }),
        ]);
        dispatch({
          type: "LOADED",
          letters,
          hebrewWords: groupByCategory(heWords),
          englishWords: groupByCategory(enWords),
        });
      } catch (e) {
        dispatch({ type: "ERROR", payload: e.message });
      }
    })();
  }, [token, requested, fetched]);

  return (
    <WordsContext.Provider value={{ ...state, requestWords }}>
      {children}
    </WordsContext.Provider>
  );
}

export const useWords = () => useContext(WordsContext);
